import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import {
	BusyIndicator,
	computeContextMenuTitle,
	ContextMenu,
	FloatingActionButton,
	Pagination,
	SearchInputForm,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig,
	TagList,
	TagModel,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog,
	useContextMenu
} from "client/Components";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	SelectableItemOptions,
	useAlerts,
	useDeepComparison,
	useForceRerender,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { contains } from "client/Data/searching";
import { localize } from "client_legacy/ndaw/culture";
import {
	generateTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ActionAndOthers,
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	IntegrationFlowAuthenticationKind,
	IntegrationFlowAuthenticationListItemModel,
	IntegrationFlowAuthenticationUsageModel,
	deleteAuthentications,
	getAuthenticationList
} from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import {
	IntegrationFlowAuthenticationOptionModel,
	IntegrationFlowStatus
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { itemStatusToLocalizationKeyMap } from "./FlowsTabContent";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { HttpRequestError } from "client/Api/httpClient";
import { useAuthenticationDialog } from "./AuthenticationDialog/AuthenticationDialog";

type AuthenticationsTabContentProps = {
	isActive: boolean;
	onActionsStateChange(
		actions: readonly ActionAndOthers<AuthenticationActionContext>[],
		context: AuthenticationActionContext
	): void;
};

export const AuthenticationsTabContent = (props: AuthenticationsTabContentProps): ReactElement => {
	const {
		isActive,
		onActionsStateChange
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [items, setItems] = useState<readonly IntegrationFlowAuthenticationListItemModel[]>([]);
	const [selectedItems, setSelectedItems] = useState<readonly IntegrationFlowAuthenticationListItemModel[]>([]);
	const [sortingState, setSortingState] = useState<DataSourceSortableState<IntegrationFlowAuthenticationListItemModel>>(
		{ propertyName: "name", direction: DataSourceSortDirection.Ascending }
	);

	const [ authenticationDialogElement, openAuthenticationDialog ] = useAuthenticationDialog();
	const { sortingFor } = useSortHandler<IntegrationFlowAuthenticationListItemModel>(sortingState, setSortingState);
	const itemsModifiersVersion: number = useDeepComparison([searchTerm, sortingState]);
	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [onContextMenu, contextMenuProps] = useContextMenu(tableRef);
	const alertManager: AlertManager = useAlerts();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [reload, loadVersion] = useForceRerender();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const {
		currentItems,
		pagination
	} = useDataSource(
		items,
		{
			pagination: {
				currentPageNumber
			},
			searchTerm,
			sorting: sortingState
		}
	);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems
		}
	);

	const createNewHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<IntegrationFlowAuthenticationOptionModel> = await openAuthenticationDialog({});

			if (!result.wasSuccessful) {
				return;
			}

			reload();
		},
		[
			openAuthenticationDialog,
			reload
		]
	);

	const deleteHandler = useCallback(
		async (items: readonly IntegrationFlowAuthenticationListItemModel[]): Promise<void> => {
			if (items.length === 0) {
				return;
			}

			const result = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToDeleteThisItem(s)"),
				confirmText: localize("_Delete"),
				trackingId: "delete-authentication-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);
			try {
				await deleteAuthentications(
					items.map(i => i.id),
					globalCancellation
				);

				alertManager.showSuccess(localize("_SuccessfullyDeletedTheItems"));
				reload();
				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				let errorMessage: string | undefined;

				if (e instanceof HttpRequestError) {
					switch (e.statusCode) {
						case 400:
							errorMessage = localize("_CantDeleteAnAuthenticationThatIsUsedForAnIntegration");
							break;
						default:
							// Nothing to do
							break;
					}
				}

				if (!errorMessage) {
					console.error(e);
					errorMessage = localize("_GeneralErrorMessage");
				}

				alertManager.showError(errorMessage);
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog,
			reload
		]
	);

	const editHandler = useCallback(
		async (item: IntegrationFlowAuthenticationListItemModel): Promise<void> => {
			const result: TryResult<IntegrationFlowAuthenticationOptionModel> = await openAuthenticationDialog({
				id: item.id
			});

			if (!result.wasSuccessful) {
				return;
			}

			reload();
		},
		[
			openAuthenticationDialog,
			reload
		]
	);

	const actionHandlers = useMemo(
		(): ActionHandlers => ({
			edit: editHandler,
			deleteItem: deleteHandler
		}),
		[
			editHandler,
			deleteHandler
		]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<AuthenticationActionContext> => {
			return getMainAction(
				createNewHandler
			);
		},
		[createNewHandler]
	);

	const selectionActions = useMemo(
		(): readonly AuthenticationSelectionAction[] => {
			return getSelectionActions({
				deselectHandler: (): void => setSelectedItems([])
			});
		},
		[]
	);

	const contextualActions = useMemo(
		(): readonly AuthenticationSelectionAction[] => [
			...listContextualActions(actionHandlers)
		],
		[
			actionHandlers
		]
	);

	const actionContext = useMemo(
		(): AuthenticationActionContext => ({
			selection: selectedItems,
			isMobile
		}),
		[
			isMobile,
			selectedItems
		]
	);

	useEffect(
		(): (() => void ) | void => {
			if (!isActive) {
				return;
			}

			onActionsStateChange(
				buildActionsForTopBar<AuthenticationActionContext, AuthenticationSelectionAction>(
					{
						contextualActions,
						selectionActions,
						mainActions: [mainAction]
					},
					actionContext.selection.length !== 0,
					isResponsive,
					false
				),
				actionContext
			);

			return (): void => {
				onActionsStateChange(
					[],
					{
						selection: [],
						isMobile: false
					}
				);
			};
		},
		[
			contextualActions,
			selectionActions,
			mainAction,
			isResponsive,
			isActive,
			actionContext,
			onActionsStateChange
		]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(itemsModifiersVersion);
			setCurrentPageNumber(0);
		},
		[itemsModifiersVersion]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(itemsModifiersVersion, currentPageNumber);
			tableRef.current
				?.scrollTo(0, 0);
		},
		[itemsModifiersVersion, currentPageNumber]
	);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(loadVersion);

			async function fetchItems(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const result: readonly IntegrationFlowAuthenticationListItemModel[] = await getAuthenticationList(signal)
						.catch(handlePageDataLoadingError);

					setItems(result);
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();
			fetchItems(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			loadVersion
		]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig | undefined => {
			const isEmpty: boolean = !isBusy
				&& currentItems.length === 0
				&& currentPageNumber === 0;

			if (isEmpty) {
				if (searchTerm) {
					return {
						icon: "magnifying-glass",
						title: localize("_NoSearchResults"),
						message: localize("_PleaseTrySearchingWithAnotherTerm")
					};
				}

				return {
					icon: "gear",
					title: localize("_NoAuthenticationsYet"),
					message: localize("_ThereAreNoAuthenticationsYet")
				};
			}

			return undefined;
		},
		[
			currentPageNumber,
			isBusy,
			searchTerm,
			currentItems.length
		]
	);

	return (
		<>
			{isBusy && <BusyIndicator />}
			<SettingsPage.Section height="stretch">
				<SettingsPage.SearchSection>
					<SearchInputForm
						onChange={setSearchTerm}
						trackingId="search-term"
						icon="filter"
						height="large"
						placeholder={(
							<SearchPlaceholder
								kind="filter"
								properties={[localize("_Name")]}
							/>
						)}
					/>
				</SettingsPage.SearchSection>
				<SettingsPage.Section
					height="stretch"
				>
					<TableView
						multiSelectable={multiSelection}
						trackingId="authentications-table"
						items={currentItems}
						columns={[
							{
								title: localize("_Name"),
								trackingId: "name",
								width: "fill",
								sortable: sortingFor("name")
							},
							{
								title: localize("_Type"),
								trackingId: "kind",
								width: "fill",
								sortable: sortingFor("kind")
							},
							{
								title: localize("_Integrations"),
								trackingId: "integrations",
								width: "fill",
								isExpensiveToRender: true
							}
						]}
						ref={tableRef}
						height="stretch"
						itemKey="id"
						noResultConfig={noResultConfig}
					>
						{item => {
							const selectableOptions: SelectableItemOptions = selectionFor(item);

							return (
								<TableView.Row
									selectable={selectableOptions}
									trackingId={item.id.toString()}
									onContextMenu={onContextMenu}
									onExecute={() => editHandler(item)}
								>
									<TableView.Cell
										tooltip={item.name}
										shouldExecuteOnClick
									>
										{item.name}
									</TableView.Cell>
									<TableView.Cell>
										{localize(itemKindToLocalizationKeyMap[item.kind])}
									</TableView.Cell>
									<TableView.Cell>
										<TagList
											trackingId={generateTrackingId(`integrations-tag-list|${item.id}`)}
											tags={item.usages
													.map(convertToTag)}
											showMoreTagsColor="secondary"
											dialogTitle={localize("_Integrations")}
										/>
									</TableView.Cell>
								</TableView.Row>
							);
						}}
					</TableView>
					<Pagination
						{...pagination}
						onPageNumberChanged={setCurrentPageNumber}
					/>
					{ contextMenuProps && (
						<ContextMenu
							{...contextMenuProps}
							actions={contextualActions}
							context={actionContext}
							trackingId={generateTrackingId("authentication-overview-context-menu")}
							title={computeContextMenuTitle(
								actionContext.selection,
								(x) => x.name
							)}
						/>
					)}
				</SettingsPage.Section>
			</SettingsPage.Section>
			<div className="preserve-space-for-mobile-plus-button"/>
			{isActive && mainAction.canExecute(actionContext) && (
				<FloatingActionButton
					icon="plus"
					onClick={() => mainAction.execute(actionContext)}
					trackingId="create-new-fab"
				/>
			)}
			{confirmationDialog}
			{authenticationDialogElement}
		</>
	);
};
AuthenticationsTabContent.displayName = "AuthenticationsTabContent";

function convertToTag(
	integration: IntegrationFlowAuthenticationUsageModel
): TagModel<number> {
	const {
		integrationId,
		status,
		name
	} = integration;

	return {
		text: status === IntegrationFlowStatus.Draft
			? `${name} (${localize(itemStatusToLocalizationKeyMap[IntegrationFlowStatus.Draft])})`
			: name,
		value: integrationId,
		color: "secondary"
	};
}

const itemKindToLocalizationKeyMap: {[key in IntegrationFlowAuthenticationKind]: LocalizationKey} = {
	[IntegrationFlowAuthenticationKind.ApiKey]: "_API",
	[IntegrationFlowAuthenticationKind.OAuth2]: "_OAuth2",
	[IntegrationFlowAuthenticationKind.Basic]: "_BasicAuth",
	[IntegrationFlowAuthenticationKind.ClientCertificate]: "_ClientCertificate",
	[IntegrationFlowAuthenticationKind.Afas]: "_AfasAuthentication"
};

type ActionHandlers = {
	edit(item: IntegrationFlowAuthenticationListItemModel): void;
	deleteItem(items: readonly IntegrationFlowAuthenticationListItemModel[]): void;
};

export type AuthenticationActionContext = {
	selection: readonly IntegrationFlowAuthenticationListItemModel[];
	isMobile: boolean;
};

export type AuthenticationSelectionAction = ToolbarAction<AuthenticationActionContext>;

function listContextualActions(
	handlers: ActionHandlers
): readonly AuthenticationSelectionAction[] {
	const {
		deleteItem,
		edit
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.selection
				.length === 1,
			execute: (c) => edit(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selection
				.length > 0,
			execute: (c) => deleteItem(c.selection),
			icon: "trash-can"
		}
	];
}

function getMainAction(
	createNew: () => void
): ToolbarRegularAction<AuthenticationActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "new",
		title: localize("_New"),
		canExecute: () => true,
		execute: (_) => createNew(),
		icon: "plus",
		isPrimary: true
	};
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(
handlers: SelectionActionsHandlers
): readonly AuthenticationSelectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: c => c.selection
				.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

const useDataSource = createLocalDataSource<
	IntegrationFlowAuthenticationListItemModel
>()
	.setConfiguration({
		search: {
			doesMatchItem: searchMatchFunction,
		},
		hasPagination: true
	})
	.getHook();

function searchMatchFunction(
	item: IntegrationFlowAuthenticationListItemModel,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const {
		name
	} = item;

	return contains(name, normalizedNotEmptySearchTerm);
}